import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Phone, MapPin } from "lucide-react";
import { SITE } from "@/data/site";

export default function Hero() {
  const scrollTo = (id) => {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen pt-28 pb-20 overflow-hidden bg-[#08080A] text-white"
    >
      {/* Background: rising sun gradient with subtle Toronto silhouette */}
      <div className="absolute inset-0 z-0">
        {/* Base deep navy/black night sky at top */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0F] via-[#1A0F08] to-[#08080A]" />

        {/* Toronto skyline image as silhouette, low opacity */}
        <img
          src="https://images.pexels.com/photos/7707287/pexels-photo-7707287.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover object-center opacity-20 grayscale contrast-125 mix-blend-screen"
        />

        {/* Rising sun radial glow — anchored low-right, like the logo */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 55% at 75% 95%, rgba(255,180,60,0.55) 0%, rgba(245,166,35,0.35) 18%, rgba(212,136,6,0.18) 38%, rgba(40,18,2,0.05) 60%, transparent 80%)",
          }}
        />

        {/* Warm horizon band */}
        <div
          className="absolute inset-x-0 bottom-0 h-2/3"
          style={{
            background:
              "linear-gradient(to top, rgba(245,166,35,0.18) 0%, rgba(212,136,6,0.08) 30%, transparent 70%)",
          }}
        />

        {/* Top-down darkening for nav legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/85 via-transparent to-transparent" />
        {/* Left-side darkening so headline stays readable */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#08080A]/75 via-[#08080A]/30 to-transparent" />
      </div>

      {/* Warm sunrise glow accent (right side) */}
      <div
        className="absolute top-1/4 right-[-10%] w-[500px] h-[500px] rounded-full bg-[#F5A623]/10 blur-[120px] z-0"
        aria-hidden
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center min-h-[calc(100vh-7rem)]">
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#F5A623] font-semibold"
            data-testid="hero-eyebrow"
          >
            <span className="w-8 h-px bg-[#F5A623]" />
            Toronto · Est. 2021
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-serif text-[2.6rem] sm:text-5xl lg:text-[4.4rem] leading-[1.04] tracking-tight mt-6 silver-text"
            data-testid="hero-headline"
          >
            Build family wealth
            <br />
            with an{" "}
            <span className="italic text-[#F5A623]">educational</span>
            <br />
            approach.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-white/75 leading-relaxed"
            data-testid="hero-subcopy"
          >
            {SITE.heroSubcopy} For emerging affluent Canadian families & individuals who want clarity,
            not jargon.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-book"
              onClick={() => scrollTo("#contact")}
              className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#F5A623] text-[#0A192F] text-sm font-medium tracking-wide hover:bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
            >
              Book a Free Consultation
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-services"
              onClick={() => scrollTo("#services")}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/25 bg-white/5 backdrop-blur text-white text-sm tracking-wide hover:border-[#F5A623] hover:text-[#F5A623] transition-all duration-300"
            >
              Explore our services
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4 text-sm text-white/70"
          >
            <a href={`tel:${SITE.contact.phone}`} className="inline-flex items-center gap-2 hover:text-[#F5A623] transition-colors" data-testid="hero-phone">
              <Phone className="w-4 h-4 text-[#F5A623]" /> {SITE.contact.phone}
            </a>
            <span className="inline-flex items-center gap-2" data-testid="hero-address">
              <MapPin className="w-4 h-4 text-[#F5A623]" /> {SITE.contact.address}
            </span>
          </motion.div>
        </div>

        {/* Right side: logo + approach card */}
        <motion.aside
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="hidden lg:block lg:col-span-5 relative"
        >
          <div className="relative ml-auto w-full max-w-md">
            <div className="absolute -top-10 -left-6 w-44 h-44 rounded-full bg-[#F5A623]/25 blur-3xl" />

            {/* Logo (transparent on dark) */}
            <div className="relative mb-8 flex items-center justify-center px-6">
              <img
                src="/sunrise-logo.png"
                alt="Sunrise Wealth Management logo"
                className="w-full max-w-sm h-auto object-contain drop-shadow-[0_20px_50px_rgba(245,166,35,0.18)]"
                data-testid="hero-logo"
              />
            </div>

            {/* Approach card */}
            <div className="relative glass-card backdrop-blur-xl rounded-2xl p-7">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#F5A623] font-semibold">
                Sunrise Educational Approach
              </p>
              <h3 className="font-serif text-xl text-white mt-3 leading-snug">
                Clients should be educated <em className="text-[#F5A623]">before</em> any recommendation.
              </h3>
              <ol className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm text-[#B7BBC4]">
                {["Listen", "Need Analysis", "Educate", "Solution", "Implement", "Monitor"].map(
                  (s, i) => (
                    <li key={s} className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full border border-[#F5A623]/40 flex items-center justify-center text-[10px] text-[#F5A623] font-semibold">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {s}
                    </li>
                  )
                )}
              </ol>
            </div>
          </div>
        </motion.aside>
      </div>
    </section>
  );
}
